<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add Doctor Speciality</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Assign medical specialization to a doctor
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-medical-bag</v-icon>
                      New Speciality Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Specialities
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-medical-bag</v-icon>
                    Doctor Speciality Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.doctor_id"
                        :items="doctors"
                        :loading="loadingDoctors"
                        item-text="text"
                        item-value="value"
                        label="Select Doctor"
                        placeholder="Choose a doctor"
                        solo
                        rounded
                        :rules="[(v) => !!v || 'Doctor is required']"
                        :error-messages="errors.doctor_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                        @change="checkDuplicateCombination"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-doctor</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-avatar size="30">
                            <v-icon color="primary">mdi-doctor</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                            <v-list-item-subtitle v-if="item.email">
                              {{ item.email }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.specialization_id"
                        :items="specializations"
                        :loading="loadingSpecializations"
                        item-text="text"
                        item-value="value"
                        label="Select Specialization"
                        placeholder="Choose a specialization"
                        solo
                        rounded
                        :rules="[(v) => !!v || 'Specialization is required']"
                        :error-messages="errors.specialization_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                        @change="checkDuplicateCombination"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-avatar size="30">
                            <v-icon color="success">mdi-medical-bag</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                            <v-list-item-subtitle v-if="item.description">
                              {{ item.description }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <!-- Duplicate Warning Alert -->
                  <v-row v-if="duplicateWarning">
                    <v-col cols="12">
                      <v-alert
                        type="warning"
                        outlined
                        dense
                        class="mb-2"
                      >
                        <v-icon slot="prepend">mdi-alert</v-icon>
                        This doctor already has this specialization assigned.
                      </v-alert>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        color="success"
                        :false-value="false"
                        :true-value="true"
                        hide-details
                        class="professional-field mt-0 pt-0"
                      >
                        <template v-slot:prepend>
                          <v-icon color="grey">mdi-toggle-switch</v-icon>
                        </template>
                      </v-switch>
                      <p class="text-caption grey--text mt-1 mb-0">
                        {{ form.is_active ? 'Speciality is active and visible' : 'Speciality is inactive and hidden' }}
                      </p>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions px-3 py-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left side: Form validation status -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      :color="valid ? 'success' : 'warning'"
                      outlined
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>
                        {{ valid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                      </v-icon>
                      {{ valid ? 'Form Valid' : 'Check Required Fields' }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      Please fill all required fields before saving
                    </span>
                  </div>
                </v-col>

                <!-- Right side: Action buttons -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="resetForm"
                      class="mr-2"
                      :disabled="loading"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Reset
                    </v-btn>

                    <v-btn
                      color="primary"
                      :disabled="!valid || duplicateWarning"
                      :loading="loading"
                      @click="save"
                      class="save-button"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Doctor Speciality
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CreateDoctorSpeciality",
  data() {
    return {
      valid: false,
      loading: false,
      loadingDoctors: false,
      loadingSpecializations: false,
      duplicateWarning: false,
      form: {
        doctor_id: null,
        specialization_id: null,
        is_active: true,
      },
      doctors: [],
      specializations: [],
      errors: {},
    };
  },
  created() {
    this.getDoctors();
    this.getSpecializations();
  },
  methods: {
    async getDoctors() {
      this.loadingDoctors = true;
      try {
        const response = await axios.get("users", {
          params: { is_active: 1, rowsPerPage: 1000 },
        });
        this.doctors = response.data.data.map((doctor) => ({
          text: `${doctor.first_name} ${doctor.last_name}`,
          value: doctor.id,
          email: doctor.email,
        }));
      } catch (error) {
        console.error("Error fetching doctors:", error);
        this.$toast.error("Failed to load doctors");
      } finally {
        this.loadingDoctors = false;
      }
    },

    async getSpecializations() {
      this.loadingSpecializations = true;
      try {
        const response = await axios.get("value_lists", {
          params: { category: "medical_specialization", rowsPerPage: 1000 },
        });
        this.specializations = response.data.data.map((specialization) => ({
          text: specialization.name,
          value: specialization.id,
          description: specialization.description,
        }));
      } catch (error) {
        console.error("Error fetching specializations:", error);
        this.$toast.error("Failed to load specializations");
      } finally {
        this.loadingSpecializations = false;
      }
    },

    async checkDuplicateCombination() {
      if (this.form.doctor_id && this.form.specialization_id) {
        try {
          const response = await axios.get("doctor_specialities", {
            params: {
              doctor_id: this.form.doctor_id,
              specialization_id: this.form.specialization_id,
              rowsPerPage: 1,
            },
          });
          this.duplicateWarning = response.data.count > 0;
        } catch (error) {
          console.error("Error checking duplicate:", error);
          this.duplicateWarning = false;
        }
      } else {
        this.duplicateWarning = false;
      }
    },

    async save() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fill all required fields");
        return;
      }

      if (this.duplicateWarning) {
        this.$toast.error("This doctor-specialization combination already exists");
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        const response = await axios.post("doctor_specialities", this.form);

        if (response.data.success) {
          this.$toast.success("Doctor speciality created successfully");
          
          // Navigate to the specialities list or edit page
          this.$router.push("/doctor_specialities");
        }
      } catch (error) {
        console.error("Error creating doctor speciality:", error);
        
        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
          this.$toast.error("Please check the form data");
        } else {
          this.$toast.error("Failed to create doctor speciality");
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        doctor_id: null,
        specialization_id: null,
        is_active: true,
      };
      this.errors = {};
      this.duplicateWarning = false;
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped>
/* Form Card Styling */
.form-card {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header Styling */
.page-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-bottom: 1px solid #e0e0e0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  line-height: 1.4;
  margin-top: 2px;
}

/* Form Content Styling */
.form-content {
  background-color: #fafafa;
}

.form-section {
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

/* Professional Field Styling */
.professional-field {
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 48px;
}

.professional-field >>> .v-text-field__details {
  margin-top: 4px;
  padding-left: 12px;
}

/* Form Actions */
.form-actions {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.save-button {
  min-width: 180px;
  font-weight: 600;
}

.back-button {
  min-width: 140px;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.25rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .form-actions .v-col {
    text-align: center !important;
  }
  
  .form-actions .d-flex {
    justify-content: center !important;
  }
}

/* Success/Error State Colors */
.v-chip.success {
  background-color: #e8f5e8 !important;
  color: #2e7d32 !important;
}

.v-chip.warning {
  background-color: #fff3e0 !important;
  color: #f57c00 !important;
}

/* Focus States */
.professional-field >>> .v-input--is-focused .v-input__control {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Loading States */
.v-btn--loading {
  pointer-events: none;
}

/* Animation for form sections */
.form-section {
  animation: slideInUp 0.3s ease-out;
}

@keyframes slideInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>